import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { parivartanAPI } from '../../../services/parivartanAPI';
import AiAssistNote from '../../../components/workspace/AiAssistNote';
import { canEditTender } from '../../../utils/tenderRecordAccess';
import { formatWorkspaceDate } from '../../../utils/workspaceDates';
import { userHasPermission } from '../../../utils/permissions';
import './TenderTechnicalDesk.scss';

const blankForm = () => ({
  id: '',
  sectionKey: '',
  title: '',
  content: '',
  dueDate: '',
  status: 'draft',
  version: 1,
  reviewComments: [],
  reusableContentRefs: []
});

const labelize = (v) =>
  String(v || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return formatWorkspaceDate(d);
};

const canRole = (user, permission, roles) => {
  if (userHasPermission(user?.permissions, permission)) return true;
  return (user?.roles || []).some((role) => roles.includes(String(role || '').trim().toUpperCase()));
};

const TenderTechnicalDesk = ({ tenderId, tender, onChanged }) => {
  const user = useSelector((s) => s.auth.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [latest, setLatest] = useState([]);
  const [sections, setSections] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [reusable, setReusable] = useState([]);
  const [pickReusable, setPickReusable] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [aiNote, setAiNote] = useState(null);

  const editable = canEditTender(tender);
  const canEditResponse = editable && canRole(
    user,
    'tender.response.edit',
    ['PLATFORM ADMINISTRATOR', 'TENDER ADMINISTRATOR', 'CONTRIBUTOR', 'BID MANAGER']
  );
  const canReview = canRole(
    user,
    'tender.response.review',
    ['PLATFORM ADMINISTRATOR', 'TENDER ADMINISTRATOR', 'BID MANAGER', 'TECHNICAL REVIEWER']
  );

  const applyPack = (data) => {
    setSections(data?.sections || []);
    setLatest(data?.latest || []);
    setSummary(data?.summary || null);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return Promise.all([
      parivartanAPI.listTechnicalSections(tenderId),
      parivartanAPI.listKnowledge().catch(() => ({ data: { data: { reusableContent: [] } } }))
    ])
      .then(([secRes, knowRes]) => {
        applyPack(secRes.data?.data || {});
        setReusable(
          (knowRes.data?.data?.reusableContent || []).filter(
            (r) => !r.isDeleted && (r.approvalStatus === 'approved' || !r.approvalStatus || r.approvalStatus === 'draft')
          )
        );
      })
      .catch(() => setError('Unable to load technical response desk'))
      .finally(() => setLoading(false));
  }, [tenderId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedVersions = useMemo(() => {
    if (!form.sectionKey && !form.id) return [];
    const key = form.sectionKey;
    return (sections || [])
      .filter((s) => s.sectionKey === key || String(s._id) === String(form.id))
      .sort((a, b) => Number(b.version || 1) - Number(a.version || 1));
  }, [sections, form.sectionKey, form.id]);

  const selectSection = (row) => {
    setForm({
      id: row._id,
      sectionKey: row.sectionKey || '',
      title: row.title || '',
      content: row.content || '',
      dueDate: row.dueDate ? String(row.dueDate).slice(0, 10) : '',
      status: row.status || 'draft',
      version: row.version || 1,
      reviewComments: row.reviewComments || [],
      reusableContentRefs: row.reusableContentRefs || []
    });
    setReviewComment('');
    setError('');
  };

  const startNew = () => {
    setForm(blankForm());
    setReviewComment('');
    setPickReusable('');
    setError('');
  };

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const canSave = canEditResponse && (!form.id || form.status === 'draft' || form.status === 'returned' || form.status === 'approved');
  const canSubmit = canEditResponse && form.id && ['draft', 'returned'].includes(form.status);
  const canApproveReturn = canReview && form.id && form.status === 'submitted';

  const saveSection = async () => {
    if (!canSave) return;
    if (!form.title.trim()) {
      setError('Section title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        id: form.id || undefined,
        title: form.title.trim(),
        content: form.content,
        dueDate: form.dueDate || null,
        sectionKey: form.sectionKey || undefined
      };
      const res = await parivartanAPI.upsertTechnicalSection(tenderId, payload);
      applyPack(res.data?.data || {});
      const section = res.data?.data?.section;
      if (section) selectSection(section);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save section');
    } finally {
      setSaving(false);
    }
  };

  const removeSection = async () => {
    if (!form.id || !canEditResponse) return;
    setSaving(true);
    setError('');
    try {
      const res = await parivartanAPI.deleteTechnicalSection(tenderId, form.id);
      applyPack(res.data?.data || {});
      startNew();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to remove section');
    } finally {
      setSaving(false);
    }
  };

  const submitSection = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      if (form.status === 'draft' || form.status === 'returned') {
        await parivartanAPI.upsertTechnicalSection(tenderId, {
          id: form.id,
          title: form.title.trim(),
          content: form.content,
          dueDate: form.dueDate || null
        });
      }
      const res = await parivartanAPI.submitTechnicalSection(tenderId, form.id);
      applyPack(res.data?.data || {});
      if (res.data?.data?.section) selectSection(res.data.data.section);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to submit section');
    } finally {
      setSaving(false);
    }
  };

  const reviewSection = async (action) => {
    if (!canApproveReturn) return;
    setSaving(true);
    setError('');
    try {
      const res = await parivartanAPI.reviewTechnicalSection(tenderId, form.id, {
        action,
        comment: reviewComment.trim()
      });
      applyPack(res.data?.data || {});
      if (res.data?.data?.section) selectSection(res.data.data.section);
      setReviewComment('');
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.message || `Unable to ${action} section`);
    } finally {
      setSaving(false);
    }
  };

  const draftWithAi = async () => {
    if (!canSave || form.status === 'submitted') return;
    if (!form.title.trim()) {
      setError('Section title is required');
      return;
    }
    const chosen = reusable.find((row) => String(row._id) === String(pickReusable));
    setSaving(true);
    setError('');
    setAiNote(null);
    try {
      const res = await parivartanAPI.draftTechnicalSection(tenderId, {
        title: form.title.trim(),
        reusableId: chosen?.approvalStatus === 'approved' ? chosen._id : undefined
      });
      const data = res.data?.data || {};
      if (data.draft) setForm((prev) => ({ ...prev, content: data.draft }));
      const libraryNote = pickReusable && chosen?.approvalStatus !== 'approved' && data.configured !== false
        ? ' Only an approved library paragraph is included.'
        : '';
      setAiNote({ ...data, message: `${data.message || ''}${libraryNote}`.trim() });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to draft this section');
    } finally {
      setSaving(false);
    }
  };

  const applyReusable = async () => {
    if (!pickReusable || !form.id || !canEditResponse) return;
    setSaving(true);
    setError('');
    try {
      const res = await parivartanAPI.applyReusableToSection(tenderId, {
        sectionId: form.id,
        contentId: pickReusable,
        mode: 'append'
      });
      applyPack(res.data?.data || {});
      if (res.data?.data?.section) selectSection(res.data.data.section);
      setPickReusable('');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to apply reusable content');
    } finally {
      setSaving(false);
    }
  };

  const downloadDocx = async () => {
    setError('');
    try {
      const res = await parivartanAPI.downloadTechnicalDocx(tenderId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `technical-response-${tenderId}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to download DOCX');
    }
  };

  const kpis = [
    { label: 'Sections', value: summary?.sectionCount ?? latest.length },
    { label: 'Draft', value: summary?.latestByStatus?.draft || 0 },
    { label: 'In review', value: summary?.latestByStatus?.submitted || 0 },
    { label: 'Returned', value: summary?.latestByStatus?.returned || 0 },
    { label: 'Approved', value: summary?.latestByStatus?.approved || 0 }
  ];

  if (loading && !latest.length) {
    return (
      <div className="ttech ttech--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="ttech">
      {error ? <Alert variant="warning">{error}</Alert> : null}
      <AiAssistNote result={aiNote} />

      <div className="ttech__banner">
        <div>
          <h3>Technical response</h3>
          <p>
            Author sections, reuse approved library text, submit for review, and export the bid DOCX.
          </p>
        </div>
        <div className="ttech__banner-actions">
          <Button size="sm" variant="outline-primary" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={downloadDocx}>
            Download DOCX
          </Button>
        </div>
      </div>

      <div className="ttech__kpis">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="ttech__kpi">
            <span>{kpi.label}</span>
            <strong>{kpi.value}</strong>
          </div>
        ))}
      </div>

      {summary?.allApproved ? (
        <Alert variant="success" className="small mb-0">
          All latest sections are approved and ready for submission pinning.
        </Alert>
      ) : null}

      <div className="ttech__layout">
        <aside className="ttech__list">
          <header className="ttech__panel-head">
            <h4>Sections</h4>
            {canEditResponse ? (
              <button type="button" onClick={startNew}>New</button>
            ) : null}
          </header>
          <ul>
            {latest.map((row) => (
              <li key={row._id}>
                <button
                  type="button"
                  className={String(form.id) === String(row._id) ? 'is-active' : undefined}
                  onClick={() => selectSection(row)}
                >
                  <strong>{row.title}</strong>
                  <span>
                    v{row.version} · {labelize(row.status)}
                  </span>
                </button>
              </li>
            ))}
            {!latest.length ? <li className="ttech__empty">No sections yet.</li> : null}
          </ul>
        </aside>

        <section className="ttech__editor">
          <header className="ttech__panel-head">
            <h4>{form.id ? `Edit · v${form.version}` : 'New section'}</h4>
            <span className={`ttech__status ttech__status--${form.status}`}>{labelize(form.status)}</span>
          </header>

          <div className="ttech__fields">
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={form.title}
                disabled={!canSave || form.status === 'submitted'}
                onChange={setField('title')}
                placeholder="e.g. Approach & methodology"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Due date</Form.Label>
              <Form.Control
                type="date"
                value={form.dueDate}
                disabled={!canSave || form.status === 'submitted'}
                onChange={setField('dueDate')}
              />
            </Form.Group>
            <Form.Group className="ttech__span-2">
              <Form.Label>Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={12}
                value={form.content}
                disabled={!canSave || form.status === 'submitted'}
                onChange={setField('content')}
                placeholder="Write the technical narrative for this section"
              />
            </Form.Group>
          </div>

          {canEditResponse && form.id && ['draft', 'returned'].includes(form.status) ? (
            <div className="ttech__reusable">
              <Form.Select
                value={pickReusable}
                onChange={(e) => setPickReusable(e.target.value)}
              >
                <option value="">Insert reusable library text…</option>
                {reusable.map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.title}{r.version ? ` · v${r.version}` : ''}
                  </option>
                ))}
              </Form.Select>
              <Button size="sm" variant="outline-primary" disabled={!pickReusable || saving} onClick={applyReusable}>
                Append
              </Button>
            </div>
          ) : null}

          {form.reusableContentRefs?.length ? (
            <p className="ttech__hint">
              Pinned reusable refs: {form.reusableContentRefs.length}
            </p>
          ) : null}

          {form.reviewComments?.length ? (
            <div className="ttech__comments">
              <h5>Review comments</h5>
              <ul>
                {form.reviewComments.map((c, i) => (
                  <li key={i}>
                    <span>{fmtDate(c.at)}</span>
                    <p>{c.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="ttech__actions">
            {canSave && form.status !== 'submitted' ? (
              <Button size="sm" variant="outline-primary" disabled={saving} onClick={draftWithAi}>
                Draft with AI
              </Button>
            ) : null}
            {canSave && form.status !== 'submitted' ? (
              <Button size="sm" disabled={saving} onClick={saveSection}>
                {form.id && form.status === 'approved' ? 'Create new version' : 'Save section'}
              </Button>
            ) : null}
            {canSubmit ? (
              <Button size="sm" variant="outline-primary" disabled={saving} onClick={submitSection}>
                Submit for review
              </Button>
            ) : null}
            {canEditResponse && form.id && ['draft', 'returned'].includes(form.status) ? (
              <Button size="sm" variant="outline-danger" disabled={saving} onClick={removeSection}>
                Remove
              </Button>
            ) : null}
          </div>

          {canApproveReturn ? (
            <div className="ttech__review">
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Review comment (required to return)"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
              <div className="ttech__actions">
                <Button size="sm" disabled={saving} onClick={() => reviewSection('approve')}>
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline-warning"
                  disabled={saving || !reviewComment.trim()}
                  onClick={() => reviewSection('return')}
                >
                  Return
                </Button>
              </div>
            </div>
          ) : null}

          {selectedVersions.length > 1 ? (
            <div className="ttech__versions">
              <h5>Version history</h5>
              <Table size="sm" className="ttech__table mb-0">
                <thead>
                  <tr>
                    <th>Ver</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {selectedVersions.map((v) => (
                    <tr key={v._id}>
                      <td>v{v.version}</td>
                      <td>{labelize(v.status)}</td>
                      <td>{fmtDate(v.updatedAt)}</td>
                      <td>
                        <button type="button" onClick={() => selectSection(v)}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}

          {!editable ? (
            <p className="ttech__hint">This pursuit is locked for editing.</p>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default TenderTechnicalDesk;
